// frontend/admin-portal/src/pages/CustomerStatement.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Input,
  Alert,
  Spinner,
  formatMoney,
} from "@fiberhome/shared-ui";
import { customerService } from "../services/customerService";
import { useStoreIdentity } from "../hooks/useStoreIdentity";

export default function CustomerStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const store = useStoreIdentity();
  const [params] = useSearchParams();

  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setMonth(today.getMonth() - 1);

  const [from, setFrom] = useState(
    params.get("from") || monthAgo.toISOString().slice(0, 10),
  );
  const [to, setTo] = useState(
    params.get("to") || today.toISOString().slice(0, 10),
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setData(await customerService.statement(id, { from, to }));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <>
        <PageHeader title="Statement" />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Statement" />
        <Alert variant="danger">{error || "Could not load statement"}</Alert>
        <Button className="mt-3" variant="outline" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </>
    );
  }

  const cur = data.currency || "USD";

  return (
    <>
      <div className="no-print">
        <PageHeader
          title="Account Statement"
          subtitle={`${data.customer.name} — ${data.period.from} to ${data.period.to}`}
          breadcrumbs={["Home", "Customers", data.customer.name, "Statement"]}
          actions={
            <div className="d-flex gap-2 flex-wrap">
              <Input
                label="From"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <Input
                label="To"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <Button variant="outline" size="sm" onClick={load}>
                Apply
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/customers/${id}`)}
              >
                Back to Customer
              </Button>
              <Button variant="primary" onClick={() => window.print()}>
                🖨 Print / Save as PDF
              </Button>
            </div>
          }
        />
      </div>

      <div className="fh-statement">
        <div className="fh-statement__header">
          <div>
            <div className="fh-statement__store-name">{store.name}</div>
            {store.address && (
              <div className="fh-statement__meta">{store.address}</div>
            )}
            {[store.phone, store.email, store.website].filter(Boolean).length >
              0 && (
              <div className="fh-statement__meta">
                {[store.phone, store.email, store.website]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="fh-statement__title">ACCOUNT STATEMENT</div>
            <div className="fh-statement__period">
              {data.period.from} — {data.period.to}
            </div>
          </div>
        </div>

        <div className="fh-statement__parties">
          <div>
            <div className="fh-statement__label">Bill To</div>
            <div style={{ fontWeight: 600 }}>{data.customer.name}</div>
            {data.customer.email && <div>{data.customer.email}</div>}
            {data.customer.phone && <div>{data.customer.phone}</div>}
            {data.customer.address && <div>{data.customer.address}</div>}
            {data.customer.city && <div>{data.customer.city}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="fh-statement__label">Opening Balance</div>
            <div className="fh-statement__big">
              {formatMoney(data.openingBalance, { symbol: cur })}
            </div>
            <div className="fh-statement__label" style={{ marginTop: 8 }}>
              Closing Balance
            </div>
            <div
              className="fh-statement__big"
              style={{ color: data.closingBalance > 0 ? "#dc3545" : "#198754" }}
            >
              {formatMoney(data.closingBalance, { symbol: cur })}
            </div>
          </div>
        </div>

        {data.lines.length === 0 ? (
          <div className="fh-statement__empty">No activity in this period.</div>
        ) : (
          <table className="fh-statement__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reference</th>
                <th>Description</th>
                <th style={{ textAlign: "right" }}>Debit</th>
                <th style={{ textAlign: "right" }}>Credit</th>
                <th style={{ textAlign: "right" }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={6} style={{ fontWeight: 600 }}>
                  Opening balance
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatMoney(data.openingBalance, { symbol: cur })}
                </td>
              </tr>
              {data.lines.map((line, i) => (
                <tr key={i}>
                  <td>
                    {new Date(line.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td>
                    <span
                      className={`fh-statement__badge fh-statement__badge--${line.type}`}
                    >
                      {line.type}
                    </span>
                  </td>
                  <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                    {line.ref}
                  </td>
                  <td>{line.description}</td>
                  <td style={{ textAlign: "right" }}>
                    {line.debit > 0
                      ? formatMoney(line.debit, { symbol: cur })
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {line.credit > 0
                      ? formatMoney(line.credit, { symbol: cur })
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>
                    {formatMoney(line.balance, { symbol: cur })}
                  </td>
                </tr>
              ))}
              <tr style={{ background: "#f8fafc" }}>
                <td colSpan={4} style={{ fontWeight: 600 }}>
                  Totals for the period
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatMoney(data.totals.debits, { symbol: cur })}
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {formatMoney(data.totals.credits, { symbol: cur })}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: 700,
                    color: data.closingBalance > 0 ? "#dc3545" : "#198754",
                  }}
                >
                  {formatMoney(data.closingBalance, { symbol: cur })}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <div className="fh-statement__footer">
          <div>
            Statement generated on {new Date(data.generatedAt).toLocaleString()}
          </div>
          <div>
            Please settle any overdue balance. Contact us with any questions.
          </div>
        </div>
      </div>
    </>
  );
}
